import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { fetchSalesReport } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SummaryCards, SalesTaxCard, InvoiceList } from "@/components/sales/SalesReportParts";

const isoDay = (d) => d.toISOString().slice(0, 10);
const monthStart = (d) => isoDay(new Date(d.getFullYear(), d.getMonth(), 1));
const PRESETS = {
  today: () => [isoDay(new Date()), isoDay(new Date())],
  month: () => [monthStart(new Date()), isoDay(new Date())],
  last: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth() - 1, 1); return [isoDay(s), isoDay(new Date(n.getFullYear(), n.getMonth(), 0))]; },
  ytd: () => [`${new Date().getFullYear()}-01-01`, isoDay(new Date())],
};
const Pill = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} className={`h-8 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${active ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`} data-testid={testId}>{children}</button>
);

export default function SalesReport() {
  const { user } = useAuth();
  const recId = user?.user_record_id || user?.record_id;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [from, setFrom] = useState(params.get("from") || PRESETS.month()[0]);
  const [to, setTo] = useState(params.get("to") || PRESETS.month()[1]);
  const [preset, setPreset] = useState(params.get("from") ? "custom" : "month");
  const [channel, setChannel] = useState(params.get("channel") || "all");
  const [status, setStatus] = useState(params.get("status") || "paid");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!recId || !from || !to) return;
    setLoading(true);
    try {
      setData(await fetchSalesReport(recId, { fromDate: from, toDate: to, channel, status, basis: "paid" }));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [recId, from, to, channel, status]);

  useEffect(() => {
    load();
    setParams({ from, to, channel, status }, { replace: true });
  }, [load, from, to, channel, status, setParams]);

  const pick = (key) => { setPreset(key); const [f, t] = PRESETS[key](); setFrom(f); setTo(t); };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-24 pt-4 space-y-4 overflow-x-hidden" data-testid="sales-report-page">
      <div className="flex items-center gap-2 print:hidden">
        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl shrink-0" onClick={() => navigate(-1)} data-testid="sales-report-back"><ArrowLeft size={18} /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-zinc-900 leading-tight">Sales Report</h1>
          <div className="text-[11px] text-zinc-500">Paid sales by payment-received date{data?.is_admin ? " · all dealers" : ""}</div>
        </div>
        <Button variant="outline" size="sm" className="rounded-xl h-9 gap-1.5 shrink-0" onClick={() => window.print()} data-testid="sales-report-print"><Printer size={14} /> Print</Button>
      </div>

      <div className="space-y-2 print:hidden" data-testid="sales-filters">
        <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] -mx-3 px-3 sm:mx-0 sm:px-0">
          {[["today", "Today"], ["month", "This Month"], ["last", "Last Month"], ["ytd", "YTD"], ["custom", "Custom"]].map(([k, l]) => (
            <Pill key={k} active={preset === k} onClick={() => (k === "custom" ? setPreset("custom") : pick(k))} testId={`sales-preset-${k}`}>{l}</Pill>
          ))}
        </div>
        {preset === "custom" && (
          <div className="grid grid-cols-2 gap-2" data-testid="sales-custom-dates">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-xl text-sm" data-testid="sales-from" />
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-xl text-sm" data-testid="sales-to" />
          </div>
        )}
        <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] -mx-3 px-3 sm:mx-0 sm:px-0">
          {[["all", "All channels"], ["web", "Web"], ["offline", "Offline"]].map(([k, l]) => <Pill key={k} active={channel === k} onClick={() => setChannel(k)} testId={`sales-channel-${k}`}>{l}</Pill>)}
          <span className="w-px bg-zinc-200 mx-1 shrink-0" />
          {[["paid", "Paid"], ["pending", "Pending"], ["all", "All statuses"]].map(([k, l]) => <Pill key={k} active={status === k} onClick={() => setStatus(k)} testId={`sales-status-${k}`}>{l}</Pill>)}
        </div>
      </div>

      <div className="hidden print:block text-sm text-zinc-600">{from} → {to} · {channel === "all" ? "All channels" : channel} · {status}</div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>
      ) : !data ? (
        <div className="text-sm text-rose-600" data-testid="sales-report-error">Could not load the report.</div>
      ) : (
        <>
          <SummaryCards summary={data.summary} />
          <SalesTaxCard summary={data.summary} byState={data.tax_by_state} period={{ from, to }} />
          <InvoiceList rows={data.rows} />
        </>
      )}
    </div>
  );
}
